import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Edit, Trash2, ClipboardList, CalendarClock, Copy, Check } from 'lucide-react'
import { useChekiForms, useDeleteChekiForm, type ChekiForm } from '@/hooks/use-cheki'
import { formatDate, formatCurrency } from '@/lib/utils'
import ChekiFormModal from '@/components/cheki/cheki-form-modal'
import { useAuthStore } from '@/stores/auth-store'
import { FeatureGate } from '@/components/feature-gate'

function buildChekiFormUrl(tenantSlug: string, formId: string): string {
  const publicBase = import.meta.env.VITE_PUBLIC_SITE_URL as string | undefined
  if (publicBase) {
    const url = new URL(publicBase)
    url.hostname = `${tenantSlug}.${url.hostname}`
    return `${url.origin}/cheki/book?form=${formId}`
  }
  return `https://${tenantSlug}.oneidol.id/cheki/book?form=${formId}`
}

const STATUS_CONFIG: Record<ChekiForm['status'], { label: string; className: string }> = {
  draft: { label: 'Draft', className: 'bg-gray-100 text-gray-600' },
  open: { label: 'Buka', className: 'bg-green-100 text-green-700' },
  closed: { label: 'Ditutup', className: 'bg-red-100 text-red-700' },
}

export default function ChekiFormsPage() {
  const { data: forms = [], isLoading } = useChekiForms()
  const deleteForm = useDeleteChekiForm()
  const tenantSlug = useAuthStore((s) => s.currentTenant?.slug)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingForm, setEditingForm] = useState<ChekiForm | undefined>()
  const [copiedId, setCopiedId] = useState<string | null>(null)

  function openCreate() {
    setEditingForm(undefined)
    setModalOpen(true)
  }

  function openEdit(form: ChekiForm) {
    setEditingForm(form)
    setModalOpen(true)
  }

  function closeModal() {
    setModalOpen(false)
    setEditingForm(undefined)
  }

  function handleDelete(form: ChekiForm) {
    if (!confirm(`Hapus form "${form.title}"? Semua pesanan di dalamnya juga akan terhapus.`)) return
    deleteForm.mutate(form.id)
  }

  async function handleCopyLink(form: ChekiForm) {
    if (!tenantSlug) return
    const url = buildChekiFormUrl(tenantSlug, form.id)
    await navigator.clipboard.writeText(url)
    setCopiedId(form.id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  if (isLoading) return <p className="text-muted-foreground">Memuat form cheki...</p>

  return (
    <FeatureGate feature="cheki_form">
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Cheki</h1>
          <p className="text-muted-foreground">Kelola form pre-order cheki</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          Buat Form Cheki
        </button>
      </div>

      {/* Empty state */}
      {forms.length === 0 && (
        <div className="rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground">
          <ClipboardList className="mx-auto h-8 w-8 mb-3 opacity-40" />
          <p className="text-sm">Belum ada form cheki. Buat form baru untuk mulai menerima pre-order.</p>
        </div>
      )}

      {/* Cards grid */}
      {forms.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {forms.map((form) => {
            const statusCfg = STATUS_CONFIG[form.status]
            const isGroup = form.quota_type === 'group'
            const members = isGroup
              ? (form.events?.event_members ?? []).map((em) => em.idol_members?.stage_name ?? em.idol_members?.name ?? '?')
              : (form.cheki_form_members ?? []).map((m) => m.idol_members?.stage_name ?? m.idol_members?.name ?? '?')

            return (
              <div
                key={form.id}
                className="rounded-xl border border-border bg-card flex flex-col"
              >
                {/* Card body */}
                <div className="p-5 flex-1 space-y-3">
                  {/* Title + status */}
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-sm leading-snug">{form.title}</p>
                    <span className={`shrink-0 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusCfg.className}`}>
                      {statusCfg.label}
                    </span>
                  </div>

                  {/* Event */}
                  {form.events && (
                    <p className="text-xs text-muted-foreground">{form.events.title}</p>
                  )}

                  {/* Open window */}
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <CalendarClock className="h-3.5 w-3.5 shrink-0" />
                    <span>
                      {formatDate(form.opens_at, { dateStyle: 'medium', timeStyle: 'short' })}
                      {' — '}
                      {formatDate(form.closes_at, { dateStyle: 'medium', timeStyle: 'short' })}
                    </span>
                  </div>

                  {/* Line-up */}
                  {members.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {members.map((name) => (
                        <span
                          key={name}
                          className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                        >
                          {name}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Prices */}
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs border-t border-border pt-2.5">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">PO</span>
                      <span className="font-semibold text-primary">{formatCurrency(form.price_idr)}</span>
                    </div>
                    {form.price_ots_idr != null && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">OTS</span>
                        <span className="font-semibold text-sky-600">{formatCurrency(form.price_ots_idr)}</span>
                      </div>
                    )}
                    {form.group_price_idr != null && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Grup PO</span>
                        <span className="font-semibold text-purple-600">{formatCurrency(form.group_price_idr)}</span>
                      </div>
                    )}
                    {form.group_price_ots_idr != null && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Grup OTS</span>
                        <span className="font-semibold text-violet-600">{formatCurrency(form.group_price_ots_idr)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Card footer */}
                <div className="flex items-center justify-between border-t border-border px-5 py-3">
                  <Link
                    to={`/cheki/${form.id}`}
                    className="text-xs font-medium text-primary hover:underline"
                  >
                    Lihat Pesanan
                  </Link>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleCopyLink(form)}
                      className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                      title="Salin link form"
                    >
                      {copiedId === form.id
                        ? <Check className="h-3.5 w-3.5 text-green-600" />
                        : <Copy className="h-3.5 w-3.5" />
                      }
                    </button>
                    <button
                      onClick={() => openEdit(form)}
                      className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                      title="Edit form"
                    >
                      <Edit className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => handleDelete(form)}
                      className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      title="Hapus form"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <ChekiFormModal open={modalOpen} onClose={closeModal} form={editingForm} />
    </div>
    </FeatureGate>
  )
}
